/**
 * RequisitesLinkSection - Секция отображения ссылки на заполнение реквизитов
 *
 * Отображается внутри RequisitesWidget:
 * - URL ссылки (усечённый) с кнопкой «Скопировать»
 * - Счётчик посещений: "2 из 3"
 * - Badge статуса: active=green, filled=blue, expired=orange, invalidated=red
 * - Кнопка «Перегенерировать» (с подтверждением)
 * - Дата создания, кто создал
 */
import React, { useState } from 'react';
import {
  Group,
  Text,
  Stack,
  Button,
  Box,
  Badge,
  ActionIcon,
  Tooltip,
  Paper,
  Divider,
} from '@mantine/core';
import {
  IconLink,
  IconCopy,
  IconCheck,
  IconRefresh,
  IconEye,
  IconUser,
  IconCalendar,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

// Цвета статусов ссылок
const LINK_STATUS_COLORS = {
  active: 'green',
  filled: 'blue',
  expired: 'orange',
  invalidated: 'red',
};

const LINK_STATUS_LABELS = {
  active: 'Активна',
  filled: 'Заполнена',
  expired: 'Просрочена',
  invalidated: 'Отменена',
};

const RequisitesLinkSection = ({ links, loading, onRegenerate }) => {
  const [copiedId, setCopiedId] = useState(null);
  const [confirmRegenerateId, setConfirmRegenerateId] = useState(null);

  // Если нет ссылок — не рендерим секцию
  if (!links || links.length === 0) {
    return null;
  }

  // Копирование URL в буфер обмена
  const handleCopy = async (link) => {
    const url = `${window.location.origin}/public/requisites/${link.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Перегенерация с подтверждением
  const handleRegenerate = (link) => {
    if (confirmRegenerateId === link.id) {
      // Второй клик — подтверждение
      setConfirmRegenerateId(null);
      onRegenerate(link.id);
    } else {
      // Первый клик — запрос подтверждения
      setConfirmRegenerateId(link.id);
      setTimeout(() => setConfirmRegenerateId(null), 3000);
    }
  };

  // Форматирование даты создания
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow',
    });
  };

  // Усечение URL для отображения
  const truncateUrl = (token) => {
    const url = `${window.location.origin}/public/requisites/${token}`;
    if (url.length > 50) {
      return url.substring(0, 47) + '...';
    }
    return url;
  };

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <IconLink size={14} color={tokens.colors.neutral[6]} />
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
          Ссылки на заполнение
        </Text>
      </Group>

      {links.map((link) => (
        <Paper
          key={link.id}
          p="sm"
          radius={12}
          style={{
            backgroundColor: tokens.colors.neutral[1],
            border: `1px solid ${tokens.colors.neutral[3]}`,
          }}
        >
          <Stack gap="xs">
            {/* Строка: URL + Скопировать + Статус */}
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                <Tooltip label={`${window.location.origin}/public/requisites/${link.token}`}>
                  <Text
                    size="xs"
                    ff="monospace"
                    c={link.status === 'active' ? tokens.colors.brand.primary : tokens.colors.neutral[6]}
                    truncate
                    style={{ maxWidth: '220px' }}
                  >
                    {truncateUrl(link.token)}
                  </Text>
                </Tooltip>

                <Tooltip label={copiedId === link.id ? 'Скопировано!' : 'Скопировать ссылку'}>
                  <ActionIcon
                    variant="subtle"
                    color={copiedId === link.id ? 'green' : 'gray'}
                    size="sm"
                    onClick={() => handleCopy(link)}
                  >
                    {copiedId === link.id ? (
                      <IconCheck size={14} />
                    ) : (
                      <IconCopy size={14} />
                    )}
                  </ActionIcon>
                </Tooltip>
              </Group>

              <Badge
                size="sm"
                variant="filled"
                radius="xl"
                color={LINK_STATUS_COLORS[link.status] || 'gray'}
              >
                {LINK_STATUS_LABELS[link.status] || link.status}
              </Badge>
            </Group>

            {/* Строка: посещения + дата + автор */}
            <Group gap="md" wrap="wrap">
              <Group gap={4}>
                <IconEye size={12} color={tokens.colors.neutral[6]} />
                <Text size="xs" c="dimmed">
                  {link.visit_count} из {link.max_visits}
                </Text>
              </Group>

              <Group gap={4}>
                <IconCalendar size={12} color={tokens.colors.neutral[6]} />
                <Text size="xs" c="dimmed">
                  {formatDate(link.created_at)}
                </Text>
              </Group>

              {link.created_by_name && (
                <Group gap={4}>
                  <IconUser size={12} color={tokens.colors.neutral[6]} />
                  <Text size="xs" c="dimmed">
                    {link.created_by_name}
                  </Text>
                </Group>
              )}
            </Group>

            {/* Кнопка перегенерации (только для активных ссылок) */}
            {link.status === 'active' && (
              <>
                <Divider color={tokens.colors.neutral[3]} />
                <Button
                  variant="subtle"
                  size="xs"
                  color={confirmRegenerateId === link.id ? 'red' : 'gray'}
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => handleRegenerate(link)}
                  loading={loading}
                  fullWidth
                >
                  {confirmRegenerateId === link.id
                    ? 'Нажмите ещё раз для подтверждения'
                    : 'Перегенерировать ссылку'}
                </Button>
              </>
            )}

            {/* Информация о заполнении (для заполненных ссылок) */}
            {link.status === 'filled' && link.filled_at && (
              <>
                <Divider color={tokens.colors.neutral[3]} />
                <Group gap="xs">
                  <IconCheck size={12} color={tokens.colors.semantic.success.primary} />
                  <Text size="xs" c="dimmed">
                    Заполнено: {formatDate(link.filled_at)}
                    {link.filled_company_name && ` • ${link.filled_company_name}`}
                  </Text>
                </Group>
              </>
            )}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

export default RequisitesLinkSection;
